/** The side effects `writeImageWithFallback` needs; injected so the logic runs without a browser. */
export interface ClipboardWriteDeps {
  /** Writes ONE clipboard entry holding `image/png` (the promise) and `text/plain` (the marker). */
  write(png: Promise<Blob>, text: string): Promise<void>;
  /** Writes the marker alone, as the board did before images were copied. */
  writeText(text: string): Promise<void>;
  /** False once a newer copy has started; a superseded copy must not touch the clipboard. */
  isCurrent(): boolean;
  /** Reports a failure loudly (the console in the element). */
  warn(message: string, error: unknown): void;
}

/** How a copy ended: picture written, marker-only fallback, overtaken by a newer copy, or nothing. */
export type ClipboardWriteOutcome = 'image' | 'fallback' | 'superseded' | 'failed';

/**
 * Puts picture + marker on the system clipboard and falls back to the marker alone.
 *
 * `write` is called synchronously, before anything is awaited: the clipboard API needs the
 * user gesture of the Ctrl+C / button press, and an earlier `await` would lose it. The picture
 * itself is a promise, so fetching and encoding may finish later (measured up to 30 s).
 *
 * When the picture fails, the browser drops the whole entry, marker included (measured
 * 2026-10-28) — hence the fallback. It runs only while this copy is still the latest one;
 * otherwise an older, slower copy would overwrite a newer one (measured in review).
 */
export async function writeImageWithFallback(
  deps: ClipboardWriteDeps,
  png: Promise<Blob>,
  text: string,
): Promise<ClipboardWriteOutcome> {
  const writing = deps.write(png, text);
  try {
    await writing;
    return 'image';
  } catch (error) {
    if (!deps.isCurrent()) return 'superseded';
    deps.warn('[o2d-whiteboard] copying the image to the clipboard failed — copying the board objects only', error);
    try {
      await deps.writeText(text);
      return 'fallback';
    } catch (fallbackError) {
      deps.warn('[o2d-whiteboard] copying to the clipboard failed — only the board-internal copy is kept', fallbackError);
      return 'failed';
    }
  }
}
